import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { MapPinIcon, CoffeeIcon, TrainFrontIcon } from 'lucide-react';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
const markerIcon = new L.Icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});
// Coordinates for Mezírka 3, Brno
const VILLA_POSITION: [number, number] = [49.2005, 16.6045];
interface LocationSectionProps {
  theme: 'dark' | 'light';
}
export function LocationSection({
  theme
}: LocationSectionProps) {
  const sectionRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(sectionRef, {
    once: true,
    margin: '-80px'
  });
  const tileUrl = theme === 'dark' ? 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png' : 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png';
  return <section id="location" ref={sectionRef} className="w-full bg-villa-bg py-24 md:py-32 px-6 md:px-10 transition-colors duration-500" aria-label="Lokalita projektu" data-id="element-83">
      <div className="max-w-7xl mx-auto" data-id="element-84">
        <motion.p initial={{
        opacity: 0,
        y: 16
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.7,
        ease: 'easeOut'
      }} className="text-villa-gold text-[12px] tracking-[0.3em] uppercase font-inter font-light mb-6" data-id="element-85">
          Lokalita
        </motion.p>

        <motion.h2 initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.7,
        delay: 0.1,
        ease: 'easeOut'
      }} className="font-playfair text-villa-text text-3xl md:text-4xl lg:text-5xl font-medium mb-16 leading-tight" data-id="element-86">
          Vše na dosah ruky
        </motion.h2>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16" data-id="element-87">
          {/* Description */}
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.7,
          delay: 0.2,
          ease: 'easeOut'
        }} data-id="element-88">
            <p className="font-inter text-villa-text/60 text-base leading-[1.9] font-light mb-10" data-id="element-89">
              Ulice Mezírka se nachází v samém srdci Brna, jen pár kroků od
              historického centra. Tato prestižní adresa nabízí dokonalou
              kombinaci klidného rezidenčního bydlení a okamžité dostupnosti
              veškeré občanské vybavenosti, kultury i gastronomie.
            </p>

            <div className="space-y-8" data-id="element-90">
              <div className="flex items-start gap-5" data-id="element-91">
                <MapPinIcon className="w-5 h-5 text-villa-gold mt-1 flex-shrink-0" strokeWidth={1.2} aria-hidden="true" data-id="element-92" />
                <div data-id="element-93">
                  <p className="font-inter text-villa-text text-sm font-medium mb-1" data-id="element-94">
                    Historické centrum
                  </p>
                  <p className="font-inter text-villa-text/40 text-sm font-light" data-id="element-95">
                    Pěší dostupnost do centra města během několika minut
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-5" data-id="element-96">
                <CoffeeIcon className="w-5 h-5 text-villa-gold mt-1 flex-shrink-0" strokeWidth={1.2} aria-hidden="true" data-id="element-97" />
                <div data-id="element-98">
                  <p className="font-inter text-villa-text text-sm font-medium mb-1" data-id="element-99">
                    Kavárny a restaurace
                  </p>
                  <p className="font-inter text-villa-text/40 text-sm font-light" data-id="element-100">
                    Nejlepší brněnské podniky v bezprostředním okolí
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-5" data-id="element-101">
                <TrainFrontIcon className="w-5 h-5 text-villa-gold mt-1 flex-shrink-0" strokeWidth={1.2} aria-hidden="true" data-id="element-102" />
                <div data-id="element-103">
                  <p className="font-inter text-villa-text text-sm font-medium mb-1" data-id="element-104">
                    Dopravní uzel
                  </p>
                  <p className="font-inter text-villa-text/40 text-sm font-light" data-id="element-105">
                    Skvělá dostupnost MHD i napojení na hlavní tahy
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Map */}
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.7,
          delay: 0.3,
          ease: 'easeOut'
        }} className="h-[400px] lg:h-full min-h-[400px] overflow-hidden rounded-sm" data-id="element-106">
            <MapContainer center={VILLA_POSITION} zoom={15} scrollWheelZoom={false} style={{
            height: '100%',
            width: '100%'
          }} attributionControl={true} key={theme} data-id="element-107">
              <TileLayer attribution='&copy; <a href="https://carto.com/">CARTO</a>' url={tileUrl} data-id="element-108" />
              <Marker position={VILLA_POSITION} icon={markerIcon} data-id="element-109">
                <Popup data-id="element-110">
                  <span className="font-inter text-sm font-medium" data-id="element-111">
                    Mezírka 3, Brno
                  </span>
                </Popup>
              </Marker>
            </MapContainer>
          </motion.div>
        </div>
      </div>
    </section>;
}